/**
 * Deterministic sfxr/jsfxr synthesis — the ONE source of every bank entry's
 * bytes, shared by the bank generator (`tools/audio-bank`) and the runtime
 * (`sfxBank.ts`), which renders the same parameters in the browser and hands
 * the bytes to the engine to verify against the entry's pinned digest.
 *
 * This is the sfxr parameter model (DrPetter's sfxr, as popularised by the
 * jsfxr port): one oscillator (square with duty sweep / sawtooth / sine /
 * noise), an attack-sustain-punch-decay envelope, frequency slide + delta
 * slide, vibrato, an arpeggio step, a resonant low-pass and a high-pass, a
 * phaser, and a repeat restart. Those parameters — not a recording and not a
 * provider — are what `tools/audio-bank/bank-manifest.json` reviews.
 *
 * WHY THIS IS A PROJECT-AUTHORED IMPLEMENTATION rather than the `jsfxr`
 * package: a digest-pinned bank needs BYTE-STABLE output, and jsfxr fills its
 * noise buffer from `Math.random()`. Two properties are therefore engineered
 * here and are the reason the pin holds — in Node when the bank is generated
 * AND in every browser that plays it:
 *
 *   1. Noise comes from a seeded xorshift32 (integer ops only), seeded from the
 *      entry id, so an entry's noise is the same bytes on every run.
 *   2. NO transcendental is used anywhere. `Math.sin` is not required to be
 *      bit-identical between engines, so the sine oscillator and the vibrato
 *      use a polynomial approximation built from +, -, *, / and `Math.floor`
 *      only — all IEEE-754 exact. It is audibly a sine (|error| < 1e-3) and it
 *      is reproducible by construction rather than by luck.
 *
 * Bytes out are canonical PCM16 mono WAV, the same container `pcmSfx.ts`
 * reads. No Node API is used, so this module runs unchanged in the browser.
 */

/** Output format. Mono 16-bit PCM; 44.1 kHz because the sfxr period math is
 *  expressed in samples, so the sample rate IS part of a cue's pitch. */
export const BANK_SAMPLE_RATE = 44100;
export const BANK_CHANNELS = 1;

/** sfxr's 8x oscillator supersampling per output sample. */
const SUPERSAMPLE = 8;
/** Hard ceiling on one cue's length, mirroring the curated-SFX 2 s bound. */
const MAX_SAMPLES = BANK_SAMPLE_RATE * 2;

/** The closed set of oscillator shapes an entry may declare. */
export const WAVE_TYPES = ['square', 'sawtooth', 'sine', 'noise'] as const;
export type SfxrWaveType = (typeof WAVE_TYPES)[number];

/** Every sfxr parameter. A bank entry declares only the parameters it actually
 *  shapes; the rest take {@link SFXR_DEFAULTS}. */
export interface SfxrParams {
  waveType: SfxrWaveType;
  /** Envelope, each 0..1 (sfxr p_env_*). */
  attack: number;
  sustain: number;
  punch: number;
  decay: number;
  /** Pitch. baseFreq/freqLimit 0..1, ramps -1..1 (sfxr p_freq_*). */
  baseFreq: number;
  freqLimit: number;
  freqRamp: number;
  freqDeltaRamp: number;
  /** Vibrato, 0..1. */
  vibratoStrength: number;
  vibratoSpeed: number;
  /** One arpeggio step: multiplier amount -1..1, speed 0..1. */
  arpeggioMod: number;
  arpeggioSpeed: number;
  /** Square duty 0..1 and its sweep -1..1 (square only). */
  duty: number;
  dutyRamp: number;
  /** Restart-the-period repeat, 0..1 (0 = never). */
  repeatSpeed: number;
  /** Phaser offset/ramp, -1..1. */
  phaserOffset: number;
  phaserRamp: number;
  /** Resonant low-pass: cutoff 0..1 (1 = bypass), ramp -1..1, resonance 0..1. */
  lowPassFreq: number;
  lowPassRamp: number;
  lowPassResonance: number;
  /** High-pass: cutoff 0..1, ramp -1..1. */
  highPassFreq: number;
  highPassRamp: number;
}

/** A numeric sfxr parameter name (everything but the wave shape). */
export type SfxrNumericParam = Exclude<keyof SfxrParams, 'waveType'>;

/** Every sfxr parameter, with its default, so a reviewer reads the difference
 *  from silence-plus-defaults rather than 20 numbers. */
export const SFXR_DEFAULTS: Readonly<SfxrParams> = Object.freeze({
  waveType: 'square',
  attack: 0,
  sustain: 0.1,
  punch: 0,
  decay: 0.3,
  baseFreq: 0.3,
  freqLimit: 0,
  freqRamp: 0,
  freqDeltaRamp: 0,
  vibratoStrength: 0,
  vibratoSpeed: 0,
  arpeggioMod: 0,
  arpeggioSpeed: 0,
  duty: 0,
  dutyRamp: 0,
  repeatSpeed: 0,
  phaserOffset: 0,
  phaserRamp: 0,
  lowPassFreq: 1,
  lowPassRamp: 0,
  lowPassResonance: 0,
  highPassFreq: 0,
  highPassRamp: 0,
});

/** Declared bounds for every parameter. The generator refuses an entry outside
 *  them, so "bounded variation" is enforced rather than asserted in prose, and
 *  a family/variant transform clamps its result against the SAME table rather
 *  than guessing a parameter's sign from its name. */
export const PARAM_BOUNDS: Readonly<Record<SfxrNumericParam, readonly [number, number]>> = Object.freeze({
  attack: [0, 1], sustain: [0, 1], punch: [0, 1], decay: [0, 1],
  baseFreq: [0, 1], freqLimit: [0, 1], freqRamp: [-1, 1], freqDeltaRamp: [-1, 1],
  vibratoStrength: [0, 1], vibratoSpeed: [0, 1],
  arpeggioMod: [-1, 1], arpeggioSpeed: [0, 1],
  duty: [0, 1], dutyRamp: [-1, 1],
  repeatSpeed: [0, 1], phaserOffset: [-1, 1], phaserRamp: [-1, 1],
  lowPassFreq: [0, 1], lowPassRamp: [-1, 1], lowPassResonance: [0, 1],
  highPassFreq: [0, 1], highPassRamp: [-1, 1],
});

function isWaveType(value: unknown): value is SfxrWaveType {
  return typeof value === 'string' && (WAVE_TYPES as readonly string[]).includes(value);
}

/** Resolve a declared partial parameter set against the defaults, rejecting an
 *  unknown key or an out-of-bounds value. Returns a complete parameter set. */
export function resolveParams(declared: Readonly<Record<string, unknown>>, label: string): SfxrParams {
  const params: SfxrParams = { ...SFXR_DEFAULTS };
  for (const [key, value] of Object.entries(declared)) {
    if (!Object.hasOwn(SFXR_DEFAULTS, key)) throw new Error(`${label}: unknown sfxr parameter ${key}`);
    if (key === 'waveType') {
      if (!isWaveType(value)) throw new Error(`${label}: waveType must be one of ${WAVE_TYPES.join(', ')}`);
      params.waveType = value;
      continue;
    }
    const numeric = key as SfxrNumericParam;
    if (typeof value !== 'number' || !Number.isFinite(value)) throw new Error(`${label}.${key} must be a finite number`);
    const [min, max] = PARAM_BOUNDS[numeric];
    if (value < min || value > max) throw new Error(`${label}.${key} must be within ${min}..${max} (got ${value})`);
    params[numeric] = value;
  }
  return params;
}

/** xorshift32 — integer-only, so the noise bytes are identical everywhere. */
function prng(seedText: string): () => number {
  // FNV-1a over the seed text, forced non-zero (xorshift is stuck at 0).
  let hash = 0x811c9dc5;
  for (let i = 0; i < seedText.length; i++) {
    hash ^= seedText.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  let state = hash === 0 ? 0x9e3779b9 : hash;
  return () => {
    state ^= state << 13; state >>>= 0;
    state ^= state >>> 17;
    state ^= state << 5; state >>>= 0;
    // [-1, 1) from the top 24 bits — no float transcendental involved.
    return (state >>> 8) / 8388608 - 1;
  };
}

/**
 * sin(2π·turns) from arithmetic only: the parabolic approximation plus the
 * standard one-pass refinement. |error| < 1.1e-3, and every operation is
 * IEEE-754 exact, which is what makes the bank digest reproducible.
 */
function sinTurns(turns: number): number {
  const t = turns - Math.floor(turns);
  const x = t * 2 - 1;
  const parabola = 4 * x * (1 - (x < 0 ? -x : x));
  return parabola + 0.225 * (parabola * (parabola < 0 ? -parabola : parabola) - parabola);
}

function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}

/**
 * Render one parameter set to mono float samples. A faithful transcription of
 * sfxr's `SynthSample`, with the two determinism substitutions above.
 */
export function renderSamples(params: Readonly<SfxrParams>, seedText: string): number[] {
  const p = params;
  const random = prng(seedText);

  let fperiod = 100 / (p.baseFreq * p.baseFreq + 0.001);
  const fmaxperiod = 100 / (p.freqLimit * p.freqLimit + 0.001);
  let period = Math.trunc(fperiod);
  let fslide = 1 - p.freqRamp * p.freqRamp * p.freqRamp * 0.01;
  const fdslide = -p.freqDeltaRamp * p.freqDeltaRamp * p.freqDeltaRamp * 0.000001;

  let squareDuty = 0.5 - p.duty * 0.5;
  const squareSlide = -p.dutyRamp * 0.00005;

  const arpMod = p.arpeggioMod >= 0
    ? 1 - p.arpeggioMod * p.arpeggioMod * 0.9
    : 1 + p.arpeggioMod * p.arpeggioMod * 10;
  let arpTime = 0;
  let arpLimit = p.arpeggioSpeed === 1
    ? 0
    : Math.trunc((1 - p.arpeggioSpeed) * (1 - p.arpeggioSpeed) * 20000) + 32;

  let fltp = 0;
  let fltdp = 0;
  let fltw = p.lowPassFreq * p.lowPassFreq * p.lowPassFreq * 0.1;
  const fltwD = 1 + p.lowPassRamp * 0.0001;
  let fltdmp = 5 / (1 + p.lowPassResonance * p.lowPassResonance * 20) * (0.01 + fltw);
  if (fltdmp > 0.8) fltdmp = 0.8;
  let fltphp = 0;
  let flthp = p.highPassFreq * p.highPassFreq * 0.1;
  const flthpD = 1 + p.highPassRamp * 0.0003;

  let vibPhase = 0;
  const vibSpeed = p.vibratoSpeed * p.vibratoSpeed * 0.01;
  const vibAmp = p.vibratoStrength * 0.5;

  const envLength = [
    Math.trunc(p.attack * p.attack * 100000),
    Math.trunc(p.sustain * p.sustain * 100000),
    Math.trunc(p.decay * p.decay * 100000),
  ] as const;
  let envStage = 0;
  let envTime = 0;
  let envVol = 0;

  let fphase = p.phaserOffset * p.phaserOffset * 1020 * (p.phaserOffset < 0 ? -1 : 1);
  let iphase = Math.min(1023, Math.abs(Math.trunc(fphase)));
  const fdphase = p.phaserRamp * p.phaserRamp * (p.phaserRamp < 0 ? -1 : 1);
  let ipp = 0;
  const phaserBuffer = new Float64Array(1024);

  const noiseBuffer = new Float64Array(32);
  for (let i = 0; i < 32; i++) noiseBuffer[i] = random();

  let repTime = 0;
  const repLimit = p.repeatSpeed === 0
    ? 0
    : Math.trunc((1 - p.repeatSpeed) * (1 - p.repeatSpeed) * 20000) + 32;

  let phase = 0;
  const out: number[] = [];
  for (let n = 0; n < MAX_SAMPLES; n++) {
    if (repLimit !== 0 && ++repTime >= repLimit) {
      repTime = 0;
      fperiod = 100 / (p.baseFreq * p.baseFreq + 0.001);
      period = Math.trunc(fperiod);
      fslide = 1 - p.freqRamp * p.freqRamp * p.freqRamp * 0.01;
      squareDuty = 0.5 - p.duty * 0.5;
      arpTime = 0;
      arpLimit = p.arpeggioSpeed === 1
        ? 0
        : Math.trunc((1 - p.arpeggioSpeed) * (1 - p.arpeggioSpeed) * 20000) + 32;
    }
    if (arpLimit !== 0 && ++arpTime >= arpLimit) {
      arpLimit = 0;
      fperiod *= arpMod;
    }
    fslide += fdslide;
    fperiod *= fslide;
    if (fperiod > fmaxperiod) {
      fperiod = fmaxperiod;
      if (p.freqLimit > 0) break;
    }
    let rfperiod = fperiod;
    if (vibAmp > 0) {
      vibPhase += vibSpeed;
      // sfxr advances the vibrato in radians; in turns that is /(2π).
      rfperiod = fperiod * (1 + sinTurns(vibPhase * 0.15915494309189535) * vibAmp);
    }
    period = Math.trunc(rfperiod);
    if (period < 8) period = 8;
    squareDuty = clamp(squareDuty + squareSlide, 0, 0.5);

    if (++envTime > envLength[envStage as 0 | 1 | 2]!) {
      envTime = 0;
      if (++envStage === 3) break;
    }
    if (envStage === 0) envVol = envLength[0] === 0 ? 1 : envTime / envLength[0];
    else if (envStage === 1) envVol = envLength[1] === 0 ? 1 + 2 * p.punch : 1 + (1 - envTime / envLength[1]) * 2 * p.punch;
    else envVol = envLength[2] === 0 ? 0 : 1 - envTime / envLength[2];

    fphase += fdphase;
    iphase = Math.min(1023, Math.abs(Math.trunc(fphase)));
    if (flthpD !== 0) flthp = clamp(flthp * flthpD, 0.00001, 0.1);

    let ssample = 0;
    for (let i = 0; i < SUPERSAMPLE; i++) {
      let sample = 0;
      if (++phase >= period) {
        phase %= period;
        if (p.waveType === 'noise') for (let k = 0; k < 32; k++) noiseBuffer[k] = random();
      }
      const fp = phase / period;
      if (p.waveType === 'square') sample = fp < squareDuty ? 0.5 : -0.5;
      else if (p.waveType === 'sawtooth') sample = 1 - fp * 2;
      else if (p.waveType === 'sine') sample = sinTurns(fp);
      else sample = noiseBuffer[Math.trunc(fp * 32)]!;

      const pp = fltp;
      fltw = clamp(fltw * fltwD, 0, 0.1);
      if (p.lowPassFreq !== 1) {
        fltdp += (sample - fltp) * fltw;
        fltdp -= fltdp * fltdmp;
      } else {
        fltp = sample;
        fltdp = 0;
      }
      fltp += fltdp;

      fltphp += fltp - pp;
      fltphp -= fltphp * flthp;
      sample = fltphp;

      phaserBuffer[ipp & 1023] = sample;
      sample += phaserBuffer[(ipp - iphase + 1024) & 1023]!;
      ipp = (ipp + 1) & 1023;

      ssample += sample * envVol;
    }
    // UNSCALED and UNCLAMPED on purpose: sfxr's `sound_vol` is a playback
    // slider, and it interacts with the filter chain badly enough that a
    // reviewed number there does not predict the resulting level. The bank
    // declares loudness as a TARGET PEAK per cue instead and the generator
    // normalises to it, which is why nothing here may pre-scale or clip.
    out.push(ssample / SUPERSAMPLE);
  }
  return out;
}

/** Scale samples so the loudest reaches `targetPeak`. The ONE loudness rule in
 *  the bank: authored parameters shape timbre, this sets level. A cue that
 *  renders effectively silent is a review error, not something to amplify. */
export function normalize(samples: readonly number[], targetPeak: number): number[] | null {
  const peak = peakOf(samples);
  if (peak < 0.01) return null;
  const gain = targetPeak / peak;
  return samples.map((sample) => sample * gain);
}

/** Canonical PCM16 mono WAV bytes for the given float samples. The returned
 *  view owns exactly its buffer, so `.buffer` is the whole file. */
export function encodeWav(samples: readonly number[]): Uint8Array<ArrayBuffer> {
  const bytes = new Uint8Array(new ArrayBuffer(44 + samples.length * 2));
  const view = new DataView(bytes.buffer);
  const tag = (at: number, value: string): void => {
    for (let i = 0; i < value.length; i++) bytes[at + i] = value.charCodeAt(i);
  };
  tag(0, 'RIFF');
  view.setUint32(4, bytes.byteLength - 8, true);
  tag(8, 'WAVE');
  tag(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, BANK_CHANNELS, true);
  view.setUint32(24, BANK_SAMPLE_RATE, true);
  view.setUint32(28, BANK_SAMPLE_RATE * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  tag(36, 'data');
  view.setUint32(40, samples.length * 2, true);
  for (let i = 0; i < samples.length; i++) {
    // Truncate toward zero after a defensive clamp: one deterministic rule, no
    // banker's-rounding question at the half-LSB boundary, and normalisation
    // already put the peak below 1 so the clamp never fires for a bank entry.
    view.setInt16(44 + i * 2, Math.trunc(clamp(samples[i]!, -1, 1) * 32767), true);
  }
  return bytes;
}

/** Peak amplitude of a rendered cue. */
function peakOf(samples: readonly number[]): number {
  let peak = 0;
  for (const sample of samples) {
    const magnitude = sample < 0 ? -sample : sample;
    if (magnitude > peak) peak = magnitude;
  }
  return peak;
}
